/**
 * Outcome Patterns — Pensieve Outcome Layer (I-A-14)
 *
 * Derives resolution patterns from existing signal lifecycle data
 * and learning events. All classifications are deterministic,
 * neutral, and informational. No user scoring, no automation.
 */

// ── Types ──────────────────────────────────────────────

export interface RawOutcomeRow {
  signal_type: string;
  total_signals: number;
  resolved_count: number;
  self_resolved_count: number;
  resolved_after_ack_count: number;
  persisted_count: number;
  retriggered_count: number;
  avg_resolution_hours: number | null;
  playbook_opened_count: number;
  playbook_step_clicked_count: number;
}

export interface OutcomePattern {
  signal_type: string;
  total_signals: number;
  resolved_after_ack_rate: number;    // 0-1
  self_resolved_rate: number;          // 0-1
  persistence_rate: number;            // 0-1
  retrigger_rate: number;              // 0-1
  playbook_usage_rate: number;         // 0-1
  avg_resolution_hours: number | null;
}

export interface OutcomeInsight {
  id: string;
  tone: 'positive' | 'neutral' | 'informational';
  label: string;
  detail: string;
  signal_type: string;
}

export interface OutcomeBadge {
  label: string;
  className: string;
}

// ── Classification ─────────────────────────────────────

function safeRate(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0;
  return Math.min(1, numerator / denominator);
}

/**
 * Transform raw RPC rows into classified outcome patterns.
 */
export function classifyOutcomes(raw: RawOutcomeRow[]): OutcomePattern[] {
  return raw.map((r) => ({
    signal_type: r.signal_type,
    total_signals: r.total_signals,
    resolved_after_ack_rate: safeRate(r.resolved_after_ack_count, r.total_signals),
    self_resolved_rate: safeRate(r.self_resolved_count, r.total_signals),
    persistence_rate: safeRate(r.persisted_count, r.total_signals),
    retrigger_rate: safeRate(r.retriggered_count, r.total_signals),
    playbook_usage_rate: safeRate(r.playbook_opened_count, r.total_signals),
    avg_resolution_hours: r.avg_resolution_hours,
  }));
}

// ── Insight Derivation ─────────────────────────────────

function formatSignalType(type: string): string {
  return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Derive neutral, informational insights from outcome patterns.
 * Each insight is deterministic — no ML, no judgment.
 */
export function deriveOutcomeInsights(patterns: OutcomePattern[]): OutcomeInsight[] {
  const insights: OutcomeInsight[] = [];

  // Only consider signal types with meaningful sample size
  const meaningful = patterns.filter((p) => p.total_signals >= 2);

  for (const p of meaningful) {
    const typeName = formatSignalType(p.signal_type);

    if (p.resolved_after_ack_rate > 0.6) {
      insights.push({
        id: `${p.signal_type}-resolved-ack`,
        tone: 'positive',
        label: 'Often Resolved After Acknowledgment',
        detail: `${typeName} signals in your scope are often resolved after being acknowledged.`,
        signal_type: p.signal_type,
      });
    }

    if (p.self_resolved_rate > 0.5) {
      insights.push({
        id: `${p.signal_type}-self-resolved`,
        tone: 'neutral',
        label: 'Often Self-Resolves',
        detail: `${typeName} signals frequently resolve without direct interaction.`,
        signal_type: p.signal_type,
      });
    }

    if (p.persistence_rate > 0.5) {
      insights.push({
        id: `${p.signal_type}-persists`,
        tone: 'informational',
        label: 'Tends to Persist',
        detail: `${typeName} signals in your scope tend to remain active beyond 7 days.`,
        signal_type: p.signal_type,
      });
    }

    if (p.retrigger_rate > 0.4) {
      insights.push({
        id: `${p.signal_type}-recurring`,
        tone: 'informational',
        label: 'Frequently Recurring',
        detail: `${typeName} signals tend to recur after resolution.`,
        signal_type: p.signal_type,
      });
    }

    if (p.playbook_usage_rate > 0.3) {
      insights.push({
        id: `${p.signal_type}-playbook`,
        tone: 'positive',
        label: 'Playbook Frequently Used',
        detail: `Playbooks are frequently opened for ${typeName} signals.`,
        signal_type: p.signal_type,
      });
    }
  }

  return insights;
}

// ── Badge for Recommendation/Signal Cards ──────────────

/**
 * Get a single outcome badge for a signal type. Returns null if no
 * notable pattern exists or insufficient data.
 */
export function getOutcomeBadge(
  signalType: string,
  patterns: OutcomePattern[],
): OutcomeBadge | null {
  const p = patterns.find((op) => op.signal_type === signalType);
  if (!p || p.total_signals < 2) return null;

  // Priority order: persistence > recurring > resolved after ack > self-resolves
  if (p.persistence_rate > 0.5) {
    return { label: 'Tends to Persist', className: 'border-warning/30 text-warning' };
  }
  if (p.retrigger_rate > 0.4) {
    return { label: 'Frequently Recurring', className: 'border-warning/30 text-warning' };
  }
  if (p.resolved_after_ack_rate > 0.6) {
    return { label: 'Often Resolves', className: 'border-primary/30 text-primary' };
  }
  if (p.self_resolved_rate > 0.5) {
    return { label: 'Often Self-Resolves', className: 'border-muted-foreground/30 text-muted-foreground' };
  }
  return null;
}

/**
 * Get an outcome context line for the "Why prioritized" section.
 * Returns null if no notable pattern.
 */
export function getOutcomeContext(
  signalType: string,
  patterns: OutcomePattern[],
): string | null {
  const p = patterns.find((op) => op.signal_type === signalType);
  if (!p || p.total_signals < 2) return null;

  const typeName = formatSignalType(signalType);

  if (p.persistence_rate > 0.5) {
    return `Similar ${typeName} signals in your scope tend to persist beyond 7 days.`;
  }
  if (p.resolved_after_ack_rate > 0.6) {
    return `Similar ${typeName} signals are often resolved after acknowledgment.`;
  }
  if (p.retrigger_rate > 0.4) {
    return `Similar ${typeName} signals tend to recur after resolution.`;
  }
  return null;
}
